import { useEffect, useRef } from "react";
import { FormikProps } from "formik";
import dayjs from "dayjs";

import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";

import { occupationFilterInitialValues } from "@/utils/initialValues/occupationFilter";
import { InternalDepartmentSelect } from "@/components/sintegrada/Selects/InternalDepartmentSelect";
import {
	defaultHospitalWardSelectOption,
	HospitalWardSelect,
} from "@/components/sintegrada/Selects/HospitalWardSelect";
import { GenderSelect } from "@/components/GenderSelect";
import { RiskClassificationSelect } from "@/components/sintegrada/Selects/RiskClassificationSelect";
import { RedAsterisk } from "@/components/RedAsterisk";
import { FormattedControl } from "@/components/FormattedControl";
import { AgeGroup } from "@/components/sintegrada/AgeGroup";
import { ApplyFilterButton } from "@/components/sintegrada/Buttons/ApplyFilterButton";
import { ExportExcelButton } from "@/components/sintegrada/Buttons/ExportExcelButton";

type Props = {
	formik: FormikProps<typeof occupationFilterInitialValues>;
	handleExportExcel: () => Promise<void>;
	disabledExportButton: boolean;
};

export function OccupationFilter({
	formik,
	handleExportExcel,
	disabledExportButton = false,
}: Readonly<Props>) {
	const { values, touched, errors } = formik;

	const isFirstRender = useRef(true);

	const selectedDepartment = values.department.label ? values.department : null;

	const minDate = dayjs().subtract(2, "years").startOf("day").format("YYYY-MM-DDTHH:mm");
	const maxDate = dayjs().endOf("day").format("YYYY-MM-DDTHH:mm");

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}

		formik.setFieldValue("hospitalWards", [defaultHospitalWardSelectOption]);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.department]);

	return (
		<Form className="mb-8" onSubmit={formik.handleSubmit}>
			<Col md={12} className="d-flex gap-4 mb-4">
				<div className="w-25">
					<Form.Label title="Data inicial" htmlFor="start-date">
						Data inicial <RedAsterisk />
					</Form.Label>

					<FormattedControl
						id="start-date"
						type="datetime-local"
						min={minDate}
						{...formik.getFieldProps("startDate")}
					/>

					{touched.startDate && errors.startDate && (
						<span className="text-danger">{errors.startDate}</span>
					)}
				</div>

				<div className="w-25">
					<Form.Label title="Data final" htmlFor="end-date">
						Data final <RedAsterisk />
					</Form.Label>

					<FormattedControl
						id="end-date"
						type="datetime-local"
						max={maxDate}
						{...formik.getFieldProps("endDate")}
					/>

					{touched.endDate && errors.endDate && (
						<span className="text-danger">{errors.endDate}</span>
					)}
				</div>

				<div className="w-25">
					<Form.Label title="Setor" htmlFor="internal-department">
						Setor <RedAsterisk />
					</Form.Label>

					<InternalDepartmentSelect
						inputId="internal-department"
						value={selectedDepartment}
						onChange={(option) => formik.setFieldValue("department", option)}
					/>

					{touched.department && errors.department && (
						<span className="text-danger">{errors.department.label}</span>
					)}
				</div>

				<div className="w-25">
					<Form.Label title="Ala" htmlFor="hospital-wards">
						Ala
					</Form.Label>

					<HospitalWardSelect
						isMulti
						departmentId={values.department.value?.id}
						value={values.hospitalWards}
						isDisabled={!values.department.value}
						onChange={(option) => formik.setFieldValue("hospitalWards", option)}
					/>
				</div>
			</Col>

			<Col md={12} className="d-flex">
				<div className="w-50 d-flex gap-4">
					<div className="w-50 d-flex gap-4">
						<Col md={6}>
							<GenderSelect
								labelClassNames="form-label"
								value={values.gender}
								onChange={(option) => formik.setFieldValue("gender", option)}
							/>
						</Col>

						<div className="w-50">
							<AgeGroup
								minAge={values.minAge}
								maxAge={values.maxAge}
								handleChangeMinAge={(value) => formik.setFieldValue("minAge", value)}
								handleChangeMaxAge={(value) => formik.setFieldValue("maxAge", value)}
							/>
						</div>
					</div>

					<div className="w-50 pe-2">
						<RiskClassificationSelect
							inputId="risk-classification"
							value={values.riskClassification}
							onChange={(option) =>
								formik.setFieldValue("riskClassification", option)
							}
						/>
					</div>
				</div>

				<div className="w-50 ps-2 d-flex align-items-end gap-4">
					<Form.Check
						id="individual"
						label="Individual"
						className="mb-1 mx-8"
						{...formik.getFieldProps("individual")}
					/>

					<ApplyFilterButton type="submit" disabled={formik.isSubmitting || !formik.isValid} />

					<ExportExcelButton
						disabledExportButton={disabledExportButton}
						onClick={handleExportExcel}
					/>
				</div>
			</Col>
		</Form>
	);
}